import { useEffect, useState, useCallback } from "react";
import {
  Input,
  Button,
  Tooltip,
  Table,
  Space,
  Popconfirm,
  Modal,
  Form,
  Tag,
} from "antd";
import {
  Trash,
  Refresh2,
  Lock,
  Unlock,
  DirectSend,
  Edit2,
} from "iconsax-react";
import Swal from "sweetalert2";
import { useTheme } from "styled-components";

import PageHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import Breadcrumb from "../../components/Breadcrumb";
import { useProfiles, DataTypeProfile } from "../../hooks/profile";
import { useAuth } from "../../hooks/auth";
import useAdminGuard from "../../hooks/useAdminGuard";

import {
  Container,
  Main,
  SearchBar,
  Content,
  HeaderContent,
  TableWrapper,
  StatusBadge,
} from "./styles";

interface UserExtended extends DataTypeProfile {
  isBlocked?: boolean;
  email: string;
}

function UserManagement() {
  const theme = useTheme();
  const { getUsers, deleteProfile, toggleUserBlock, updateUserEmail } =
    useProfiles();
  const { forgotPassword } = useAuth();

  const [busca, setBusca] = useState("");
  const [data, setData] = useState<UserExtended[]>([]);
  const [loading, setLoading] = useState(false);

  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserExtended | null>(null);
  const [form] = Form.useForm();
  useAdminGuard();

  const handleFetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const users = await getUsers();
      setData(users as UserExtended[]);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao listar usuários",
        text: "Não foi possível carregar a lista de usuários.",
      });
    } finally {
      setLoading(false);
    }
  }, [getUsers]);

  useEffect(() => {
    handleFetchUsers();
  }, [handleFetchUsers]);

  const filteredUsers = data.filter(
    (user) =>
      user.name?.toLowerCase().includes(busca.toLowerCase()) ||
      user.email?.toLowerCase().includes(busca.toLowerCase())
  );

  const handleDelete = async (uid: string) => {
    try {
      await deleteProfile(uid);
      Swal.fire("Excluído!", "Usuário removido do banco de dados.", "success");
      handleFetchUsers();
    } catch (error) {
      Swal.fire("Erro", "Falha ao excluir usuário.", "error");
    }
  };

  const handleToggleBlock = async (user: UserExtended) => {
    const newStatus = !user.isBlocked;
    try {
      await toggleUserBlock(user.uid, newStatus);
      Swal.fire(
        newStatus ? "Bloqueado!" : "Desbloqueado!",
        `O usuário foi ${
          newStatus ? "bloqueado" : "desbloqueado"
        } com sucesso.`,
        "success"
      );
      handleFetchUsers();
    } catch (error) {
      Swal.fire("Erro", "Falha ao alterar status do usuário.", "error");
    }
  };

  const handleForgotPassword = async (email: string) => {
    try {
      await forgotPassword(email);
    } catch (error) {
      // Swall is already handled in hook
    }
  };

  const showEmailModal = (user: UserExtended) => {
    setSelectedUser(user);
    form.setFieldsValue({ email: user.email });
    setIsEmailModalVisible(true);
  };

  const handleUpdateEmail = async (values: { email: string }) => {
    if (!selectedUser) return;
    try {
      await updateUserEmail(selectedUser.uid, values.email);
      Swal.fire("Sucesso", "E-mail atualizado no banco de dados.", "success");
      setIsEmailModalVisible(false);
      handleFetchUsers();
    } catch (error) {
      Swal.fire("Erro", "Falha ao atualizar e-mail.", "error");
    }
  };

  const columns = [
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: UserExtended) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{text || "Sem nome"}</span>
          <span style={{ fontSize: "14px", color: theme.colors.textSecondary }}>
            {record.email}
          </span>
        </Space>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag color={type === "admin" ? "purple" : "blue"}>
          {type?.toUpperCase() || "USER"}
        </Tag>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (record: UserExtended) => (
        <StatusBadge $blocked={record.isBlocked}>
          {record.isBlocked ? "Bloqueado" : "Ativo"}
        </StatusBadge>
      ),
    },
    {
      title: "Ações",
      key: "actions",
      render: (record: UserExtended) => (
        <Space size="small">
          <Tooltip
            title={
              record.isBlocked ? "Desbloquear Usuário" : "Bloquear Usuário"
            }
          >
            <Button
              shape="circle"
              onClick={() => handleToggleBlock(record)}
              icon={
                record.isBlocked ? <Unlock size={16} /> : <Lock size={16} />
              }
            />
          </Tooltip>

          <Tooltip title="Enviar E-mail de Senha">
            <Button
              shape="circle"
              onClick={() => handleForgotPassword(record.email)}
              icon={<DirectSend size={16} />}
            />
          </Tooltip>

          <Tooltip title="Alterar E-mail">
            <Button
              shape="circle"
              onClick={() => showEmailModal(record)}
              icon={<Edit2 size={16} />}
            />
          </Tooltip>

          <Popconfirm
            title="Deseja realmente excluir este usuário?"
            description="Isso removerá os dados do banco, mas não o acesso Firebase Auth."
            onConfirm={() => handleDelete(record.uid)}
            okText="Sim"
            cancelText="Não"
          >
            <Button shape="circle" danger icon={<Trash size={16} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Container id="page-user-management">
      <PageHeader
        title="Gestão de Usuários"
        description="Gerencie todos os usuários cadastrados na plataforma Bilíngua."
        type="private"
      />
      <Main>
        <Breadcrumb
          items={[
            { label: "Gerenciamento", link: "/manager" },
            { label: "Gestão de Usuários", active: true },
          ]}
        />
        <SearchBar>
          <Input
            placeholder="Buscar por nome ou e-mail..."
            size="large"
            allowClear
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ height: "6rem", flex: 1 }}
          />
          <Button
            type="primary"
            icon={<Refresh2 size={18} />}
            onClick={handleFetchUsers}
            loading={loading}
            style={{ height: "5rem", borderRadius: "0.8rem" }}
          >
            Atualizar
          </Button>
        </SearchBar>

        <Content>
          <HeaderContent>Lista de Usuários</HeaderContent>

          <TableWrapper>
            <Table
              columns={columns}
              dataSource={filteredUsers}
              loading={loading}
              rowKey="uid"
              pagination={{ pageSize: 10 }}
            />
          </TableWrapper>
        </Content>
      </Main>

      <Modal
        title="Alterar E-mail do Usuário"
        open={isEmailModalVisible}
        onCancel={() => setIsEmailModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdateEmail}>
          <Form.Item
            name="email"
            label="Novo E-mail"
            rules={[
              { required: true, message: "Por favor, insira o e-mail" },
              { type: "email", message: "E-mail inválido" },
            ]}
          >
            <Input size="large" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              Salvar Alteração
            </Button>
          </Form.Item>
          <p style={{ fontSize: "14px", color: theme.colors.warning }}>
            Atenção: Esta alteração afeta apenas o banco de dados. O e-mail de
            login no Firebase Auth não será alterado.
          </p>
        </Form>
      </Modal>

      <Footer />
    </Container>
  );
}

export default UserManagement;
